

import { faDownload, faEye } from '@fortawesome/pro-duotone-svg-icons';
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography, styled } from '@mui/material';
import { BasicAlert, GetFieldLookupItem, GroupedFields, useFormMetadata, useLookups } from '@ngt/opms';
import { DateTime } from 'luxon';
import { FC, useCallback } from 'react';

import { FormDefinitionType, GetImageReportUploadContentResponse, LookupImageReportDocumentType } from '../../../api/dtos';


const Subheading = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(3, 2, 3, 2)
}))

const StyledTableRow = styled(TableRow)(() => ({
    "& > td, & > th": {
        width: '33%'
    }

}))

const StyledButton = styled(Button)(({ theme }) => ({
    minWidth: 0,
    padding: theme.spacing(0, 1.25),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    flexBasis: '100%',
    height: '32px',

    '& > *': {
        opacity: 0.9
    }
}));


interface IImageReportUploadViewProps {
    data?: Omit<GetImageReportUploadContentResponse, "responseStatus">
}

const ImageReportUploadView: FC<IImageReportUploadViewProps> = ({
    data
}) => {
    const formMetadata = useFormMetadata(FormDefinitionType.ImageReportUpload);

    const {
        data: lookups
    } = useLookups(formMetadata);

    const getFileType = useCallback((fileTypeId: number | undefined) => {
        const lookupValue = GetFieldLookupItem(lookups, "ImageReportDocuments.FileTypeId", fileTypeId)?.value ?? (fileTypeId === LookupImageReportDocumentType.MRI ? 'MRI' : '');

        return lookupValue;
    }, [lookups]);

    const getFileDate = useCallback((fileDateStr: string | undefined) => {
        if (!fileDateStr) return undefined;
        return DateTime.fromISO(fileDateStr).toFormat('dd/MM/yyyy');
    }, [lookups]);

    if (!data) return null;

    return (
        <GroupedFields>
            <Subheading 
                variant="h2"
            >
                Reports
            </Subheading>
            {
                (!data.imageReportDocuments || data.imageReportDocuments.length === 0) && (
                    <BasicAlert
                        severity="info"
                        title={`No Reports`}
                        description={`No Reports have been added.`}
                        icon={faInfoCircle}
                    />
                )
            }
            {
                data.imageReportDocuments && data.imageReportDocuments.length > 0 && (
                    <Table>
                        <TableHead>
                            <StyledTableRow>
                                <TableCell>
                                    <Typography variant="body1">
                                        <strong>Type</strong>
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body1">
                                        <strong>Date</strong>
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body1">
                                        <strong>File</strong>
                                    </Typography>
                                </TableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {
                                data.imageReportDocuments?.map((document, index) => (
                                    <StyledTableRow
                                        key={index}
                                    >
                                        <TableCell>
                                            <Typography variant="body1">
                                                {getFileType(document.fileTypeId)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body1">
                                                {getFileDate(document.fileDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                style={{
                                                    display: 'flex',
                                                    minWidth: '300px',
                                                    gap: '8px'
                                                }}
                                            >
                                                <StyledButton
                                                    color="secondary" 
                                                    variant="contained"
                                                    //@ts-expect-error
                                                    component="a"
                                                    disabled={!document.fileViewUrl}
                                                    href={document.fileViewUrl}
                                                    target="_blank"
                                                    startIcon={<FontAwesomeIcon style={{ fontSize: "0.875rem" }} icon={faEye} fixedWidth/>}
                                                >
                                                    View
                                                </StyledButton>
                                                <StyledButton
                                                    color="secondary" 
                                                    variant="contained"
                                                    //@ts-expect-error
                                                    component="a"
                                                    disabled={!document.fileDownloadUrl}
                                                    href={document.fileDownloadUrl}
                                                    startIcon={<FontAwesomeIcon style={{ fontSize: "0.875rem" }} icon={faDownload} />}
                                                >
                                                    Download
                                                </StyledButton>
                                            </Box>
                                        </TableCell>
                                    </StyledTableRow>
                                ))
                            }
                        </TableBody>
                    </Table>
                )
            }
        </GroupedFields>
    );
}

export default ImageReportUploadView